import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OrderOverview() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [period, setPeriod] = useState('this_year');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/orders/overview?period=${period}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      const json = await res.json();
      setData(json);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, period, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  if (initialLoading || !data) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📦 Order Management Overview</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {['today','this_week','this_month','previous_month','this_year','all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ padding: '6px 14px', borderRadius: 6, border: period === p ? '2px solid #3b82f6' : '1px solid #d1d5db',
                background: period === p ? '#eff6ff' : '#fff', color: period === p ? '#3b82f6' : '#374151',
                fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Custom date range */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>CUSTOM:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputSm} />
        <span style={{ color: '#9ca3af' }}>→</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputSm} />
      </div>

      {/* Main Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        <SummaryCard label="Total Orders" value={data.total_orders} color="#3b82f6" isNumber />
        <SummaryCard label="Total Order Value" value={fmt(data.total_order_value)} color="#10b981" />
        <SummaryCard label="Total Cost" value={fmt(data.total_cost)} color="#f59e0b" />
        <SummaryCard label="Gross Profit" value={fmt(data.gross_profit)} color={data.gross_profit >= 0 ? '#10b981' : '#ef4444'} />
        <SummaryCard label="Avg Margin" value={data.average_margin + '%'} color="#8b5cf6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        <SummaryCard label="Total Paid" value={fmt(data.total_paid)} color="#10b981" />
        <SummaryCard label="Total Remaining" value={fmt(data.total_remaining)} color="#ef4444" />
        <SummaryCard label="Pending Fulfillment" value={data.pending_fulfillment} color="#f59e0b" isNumber />
        <SummaryCard label="Partially Fulfilled" value={data.partially_fulfilled} color="#f97316" isNumber />
        <SummaryCard label="Completed Orders" value={data.completed_count} color="#10b981" isNumber />
      </div>

      {/* System State Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        <StateCard label="Draft" count={data.draft_count} color="#9ca3af" icon="📝" />
        <StateCard label="Confirmed" count={data.confirmed_count} color="#3b82f6" icon="✅" />
        <StateCard label="Completed" count={data.completed_count} color="#10b981" icon="🎉" />
        <StateCard label="Cancelled" count={data.cancelled_count} color="#ef4444" icon="❌" />
      </div>

      {/* Custom Status Cards */}
      {Array.isArray(data.status_cards) && data.status_cards.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>Order Statuses</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {data.status_cards.map(sc => (
              <div key={sc.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>{sc.name}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>{sc.order_count}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Value: {fmt(sc.total_value)}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>Remaining: {fmt(sc.total_remaining)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Pending Actions */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>⚠️ Pending Actions</h3>
          {data.pending_actions?.length > 0 ? (
            <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'disc' }}>
              {data.pending_actions.map((a, i) => (
                <li key={i} style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>• {a}</li>
              ))}
            </ul>
          ) : (
            <div style={{ color: '#10b981', fontSize: 13 }}>✓ No pending actions</div>
          )}
        </div>

        {/* Payment / Invoice Status */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>💳 Payment & Invoicing</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Payment Status</div>
              <StatusRow label="Unpaid" count={data.unpaid_orders} color="#ef4444" />
              <StatusRow label="Partial" count={data.partially_paid_orders} color="#f59e0b" />
              <StatusRow label="Paid" count={data.paid_orders} color="#10b981" />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Invoice Status</div>
              <StatusRow label="Not Invoiced" count={data.not_invoiced} color="#9ca3af" />
              <StatusRow label="Partial" count={data.partially_invoiced} color="#f59e0b" />
              <StatusRow label="Fully Invoiced" count={data.fully_invoiced} color="#10b981" />
            </div>
          </div>
        </div>
      </div>

      {/* By Client */}
      {Array.isArray(data.by_client) && data.by_client.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>👤 Orders by Client</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>{['Client','Orders','Total Value','Paid','Remaining','Gross Profit'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.by_client.map((c, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{c.client}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{c.order_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(c.total_value)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(c.total_paid)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(c.remaining)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: c.gross_profit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(c.gross_profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Department */}
      {Array.isArray(data.by_department) && data.by_department.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>🏢 Orders by Department</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>{['Department','Orders','Total Value','Cost','Gross Profit','Margin %'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.by_department.map((d, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{d.department}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{d.order_count}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(d.total_value)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(d.total_cost)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: d.gross_profit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(d.gross_profit)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{d.margin}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#374151' }}>🕐 Recent Orders</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>{['Order #','Client','Date','Total','State','Status','Payment','Fulfillment'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {Array.isArray(data.recent_orders) && data.recent_orders.map(o => (
                <tr key={o.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{o.order_number}</td>
                  <td style={tdStyle}>{o.client_name || '—'}</td>
                  <td style={tdStyle}>{o.order_date ? o.order_date.split('T')[0] : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(o.order_total)}</td>
                  <td style={tdStyle}><Badge value={o.system_state} /></td>
                  <td style={tdStyle}>{o.status_name || '—'}</td>
                  <td style={tdStyle}><Badge value={o.payment_status} /></td>
                  <td style={tdStyle}><Badge value={o.fulfillment_status} /></td>
                </tr>
              ))}
              {(!data.recent_orders || data.recent_orders.length === 0) && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, isNumber }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: isNumber ? 28 : 20, fontWeight: 700, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}

function StateCard({ label, count, color, icon }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: '#6b7280' }}>{label}</div>
    </div>
  );
}

function StatusRow({ label, count, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
      <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color }}>{count}</span>
    </div>
  );
}

function Badge({ value }) {
  const colors = {
    draft: '#9ca3af', confirmed: '#3b82f6', completed: '#10b981', cancelled: '#ef4444',
    unpaid: '#ef4444', partially_paid: '#f59e0b', paid: '#10b981',
    not_fulfilled: '#9ca3af', partially_fulfilled: '#f59e0b', fully_fulfilled: '#10b981',
    not_invoiced: '#9ca3af', partially_invoiced: '#f59e0b', fully_invoiced: '#10b981',
  };
  const c = colors[value] || '#6b7280';
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: c + '18', color: c }}>
      {(value || '').replace(/_/g, ' ')}
    </span>
  );
}

const inputSm = { padding: '5px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', whiteSpace: 'nowrap' };
